import { addDays, subWeeks } from 'date-fns'
import { useCallback, useEffect, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Icon } from '@/common/components/Icon'
import { Header, PageTitle } from '@/components'
import {
  Button,
  Card,
  DateTimePicker,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '@/components/ui'
import Loader from '../../../common/components/Loader'
import { Currency } from '../../../common/models'
import { IColumn } from '../../../core/components/Table'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { getCurrencies } from '../../../core/services/festival-settings'
import {
  CoinVoucher,
  CoinVouchersBatchDownloadListResponse,
  VoucherPaginationParams,
  coinVouchers,
  coinVouchersBatchListDownload,
  exportVouchersHistory,
} from '../../../core/services/voucher'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'
import { RedeemVoucherModal } from './RedeemVoucherModal'
import { RedeemVouchersDownloadsTable } from './RedeemVouchersDownloadsTable'
import { RedeemVouchersTable } from './RedeemVouchersTable'
import { RedeemVoucherViewModal } from './RedeemVoucherViewModal'

export const RedeemVouchersPage: React.FC = () => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
    extraParams,
    setExtraParams,
  } = useTableParams({
    extraParams: {
      key: 'redeem',
    },
  })

  const { hasEditPermission } = useCheckPermission({ page: 'VOUCHERS' })

  const { editionId, festivalName } = useParams()

  const [showRedeemVoucherModal, setShowRedeemVoucherModal] =
    useState<boolean>(false)
  const [viewVoucher, setViewVoucher] = useState<CoinVoucher | null>(null)
  const [startDate, setStartDate] = useState<number>(
    subWeeks(new Date(), 1).setHours(0, 0, 0).valueOf(),
  )
  const [endDate, setEndDate] = useState<number>(
    addDays(new Date(), 1).setHours(23, 59, 59).valueOf(),
  )

  const [coinVouchersList, setCoinVouchersList] = useState<CoinVoucher[]>([])
  const [coinVouchersBatchDownloadList, setCoinVouchersBatchDownloadList] =
    useState<CoinVouchersBatchDownloadListResponse[]>([])
  const [key, setKey] = useState<string>(extraParams.key || 'redeem')
  const [loading, setLoading] = useState<boolean>(false)
  const [currency, setCurrency] = useState<Currency | null>(null)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const [focused, setFocused] = useState(false)

  const timer = useRef<NodeJS.Timeout | null>(null)
  const searchParams = useRef<{ [key: string]: string | null }>({})

  useEffect(() => {
    if (startDate > endDate) {
      setEndDate(new Date(startDate).setHours(23, 59, 59))
    }
  }, [startDate])

  useEffect(() => {
    if (startDate > endDate) {
      setStartDate(new Date(endDate).setHours(0, 0, 0))
    }
  }, [endDate])

  const getCurrency = useCallback(async () => {
    try {
      const response = await getCurrencies()
      if (response.status === 200) {
        setCurrency(response.data.defaultCurrency)
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    }
  }, [])

  const getData = useCallback(async () => {
    if (focused) {
      return
    }
    setLoading(true)
    try {
      if (key === 'redeem') {
        const { data, status } = await coinVouchers({
          size,
          page,
          sortBy,
          startDate,
          endDate,
          sortDirection,
          voucherCode: searchParams.current.voucherCode,
        })
        if (status === 200) {
          setCoinVouchersList(data.content)
        }
      } else if (key === 'download') {
        const { data, status } = await coinVouchersBatchListDownload({
          size,
          page,
          sortBy,
          startDate,
          endDate,
          sortDirection,
        })
        if (status === 200) {
          setCoinVouchersBatchDownloadList(data.content)
        }
      }
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [
    size,
    page,
    sortBy,
    startDate,
    endDate,
    key,
    focused,
    editionId,
    festivalName,
    sortDirection,
  ])

  useEffect(() => {
    getData()
  }, [getData, editionId, festivalName])

  const handleSearchParams = useCallback(
    async (col: IColumn, q: string | null) => {
      if (timer.current) {
        clearTimeout(timer.current)
      }

      searchParams.current = {
        ...searchParams.current,
        [col.accessor]: q,
      }

      const newTimer = setTimeout(async () => {
        setLoading(true)
        try {
          const params: VoucherPaginationParams = {
            size,
            page: 0,
            sortBy,
            startDate,
            endDate,
            sortDirection,
          }

          params.redeemCode = searchParams.current.redeemCode

          const { data, status } = await coinVouchers({
            ...params,
          })
          if (status === 200) {
            setCoinVouchersList(data.content)
            setTotalElemenets(data.totalElements)
          }
        } catch (err) {
          console.log(err)
        } finally {
          setLoading(false)
        }
      }, 2000)
      timer.current = newTimer
    },
    [page, size, sortBy, sortDirection],
  )

  useEffect(() => {
    getCurrency()
  }, [getCurrency, editionId, festivalName])

  const downloadCSV = (cssContent: string) => {
    if (!hasEditPermission) {
      return
    }
    const blob = new Blob([cssContent], { type: 'text/csv' })
    const url = URL.createObjectURL(blob)

    const a = document.createElement('a')
    a.href = url
    a.download = 'vouchers.csv' // Change the filename if needed
    document.body.appendChild(a)
    a.click()

    // Cleanup
    setTimeout(() => {
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    }, 0)
  }

  return (
    <>
      <Header
        pageTitle="Rewards"
        actions={
          hasEditPermission &&
          key === 'redeem' && (
            <>
              <>
                <Button
                  variant="primary-bordered"
                  onClick={() => {
                    setShowRedeemVoucherModal(true)
                  }}
                >
                  Add new
                  <Icon
                    icon="plus-circle"
                    width="18"
                    height="18"
                    viewBox="0 0 18 18"
                  />
                </Button>
              </>
            </>
          )
        }
      />
      {loading && <Loader />}
      <Tabs
        value={key}
        onValueChange={(newKey) => {
          setKey(newKey)
          setExtraParams((s) => ({ ...s, key: newKey }))
          setPage(1)
        }}
        className="w-full"
      >
        <TabsList className="flex mb-6">
          <div className="bg-white/10 p-2.5 rounded-[20px]">
            <TabsTrigger value="redeem">Redeem Vouchers</TabsTrigger>
            <TabsTrigger value="download">Redeem Vouchers Download</TabsTrigger>
          </div>
        </TabsList>
        <Card className="!bg-monochrome4-60 p-5 backdrop-blur-lg max-h-[calc(100svh-200px)] overflow-y-auto">
          <TabsContent value="redeem" className="mt-0">
            <PageTitle
              title="Vouchers"
              subtitle="Redeem Vouchers"
              className="mb-5"
            />
            <RedeemVouchersTable
              data={coinVouchersList}
              sortBy={sortBy || 'id'}
              setSortBy={setSortBy}
              handleSearchParams={handleSearchParams}
              onViewVoucher={(voucher) => setViewVoucher(voucher)}
            />
          </TabsContent>

          <TabsContent value="download" className="mt-0">
            <PageTitle
              title="Vouchers"
              subtitle="Redeem Vouchers Download"
              className="mb-5"
            />
            <RedeemVouchersDownloadsTable
              data={coinVouchersBatchDownloadList}
              sortBy={sortBy || 'id'}
              setSortBy={setSortBy}
              onUpdated={getData}
            />
          </TabsContent>
        </Card>
      </Tabs>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={setPage}
      />
      <RedeemVoucherModal
        showModal={showRedeemVoucherModal}
        setShowModal={setShowRedeemVoucherModal}
        onVoucherCreated={getData}
        currency={currency?.symbol || 'AED'}
      />
      <RedeemVoucherViewModal
        voucher={viewVoucher}
        open={!!viewVoucher}
        onClose={() => setViewVoucher(null)}
        onUpdated={getData}
        currency={currency?.symbol || 'AED'}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
